// Responsibility boundary:
// - owns imperative remote participant/renderer DOM management
// - keeps endpoint/media rendering details out of the main React component
// - does not manage call state or local camera lifecycle
import {MutableRefObject} from 'react';
import * as VoxImplant from 'voximplant-websdk';

type EndpointLike = {
  id: string;
  displayName: string;
  isDefault?: boolean;
  addEventListener: (event: VoxImplant.EndpointEvents, handler: (ev: any) => void) => void;
  removeEventListener: (event: VoxImplant.EndpointEvents, handler: (ev: any) => void) => void;
};

type MediaRendererLike = {
  id: string;
  kind: string;
  render: (container: HTMLElement) => void;
};

export type EndpointMediaEvent = {endpoint: EndpointLike; mediaRenderer: MediaRendererLike};
export type EndpointEvent = {endpoint: EndpointLike};
type RemoteVideoHolderRef = MutableRefObject<HTMLDivElement | null>;
type EndpointsMediaRef = MutableRefObject<Record<string, string[]>>;

type RemoteMediaParams = {
  data: EndpointMediaEvent;
  remoteVideoHolderRef: RemoteVideoHolderRef;
  endpointsMediaRef: EndpointsMediaRef;
  participantName: string;
  isConferenceCall: boolean;
};

type EndpointAddedParams = {
  data: EndpointEvent;
  remoteVideoHolderRef: RemoteVideoHolderRef;
  endpointsMediaRef: EndpointsMediaRef;
  onRemoteMediaAdded: (data: EndpointMediaEvent) => void;
  onRemoteMediaRemoved: (data: EndpointMediaEvent) => void;
  onEndpointRemoved: (data: EndpointEvent) => void;
};

type EndpointRemovedParams = {
  data: EndpointEvent;
  remoteVideoHolderRef: RemoteVideoHolderRef;
  endpointsMediaRef: EndpointsMediaRef;
  onRemoteMediaAdded: (data: EndpointMediaEvent) => void;
  onRemoteMediaRemoved: (data: EndpointMediaEvent) => void;
  onEndpointRemoved: (data: EndpointEvent) => void;
};

function getRemoteVideoHolder(remoteVideoHolderRef: RemoteVideoHolderRef) {
  return remoteVideoHolderRef.current as HTMLElement | null;
}

function fillUserInfoInVideoContainer(
  videoContainerElement: HTMLDivElement,
  participantUserId: string
) {
  const label = document.createElement('div');
  label.classList.add('label');
  label.innerText = participantUserId;
  videoContainerElement.appendChild(label);
}

function addNoVideoContainer(
  remoteVideoHolderRef: RemoteVideoHolderRef,
  endpointId: string,
  participantUserId: string
) {
  const participantContainer = document.createElement('div');
  participantContainer.classList.add('participant_no_video', `participant_no_video_${endpointId}`);

  const noVideoSign = document.createElement('div');
  noVideoSign.innerText = 'no video';

  const label = document.createElement('div');
  label.classList.add('label');
  label.innerText = participantUserId;
  noVideoSign.appendChild(label);

  participantContainer.appendChild(noVideoSign);
  getRemoteVideoHolder(remoteVideoHolderRef)
    ?.querySelector(`.participant_container_${CSS.escape(endpointId)}`)
    ?.appendChild(participantContainer);
}

function removeNoVideoContainer(remoteVideoHolderRef: RemoteVideoHolderRef, endpointId: string) {
  getRemoteVideoHolder(remoteVideoHolderRef)
    ?.querySelector(`.participant_no_video_${CSS.escape(endpointId)}`)
    ?.remove();
}

export function clearRemoteVideoHolder(remoteVideoHolderRef: RemoteVideoHolderRef) {
  const remoteVideoHolder = getRemoteVideoHolder(remoteVideoHolderRef);

  while (remoteVideoHolder?.firstChild) {
    remoteVideoHolder.removeChild(remoteVideoHolder.lastChild as ChildNode);
  }
}

export function handleRemoteMediaAdded({
  data,
  remoteVideoHolderRef,
  endpointsMediaRef,
  participantName,
  isConferenceCall,
}: RemoteMediaParams) {
  const mediaRendererKind = data.mediaRenderer.kind.replace('sharing', 'video');

  if (!data.endpoint.isDefault || !isConferenceCall) {
    endpointsMediaRef.current[data.endpoint.id].push(mediaRendererKind);

    if (
      mediaRendererKind === 'audio' &&
      !endpointsMediaRef.current[data.endpoint.id].includes('video')
    ) {
      addNoVideoContainer(remoteVideoHolderRef, data.endpoint.id, participantName);
    }

    if (
      mediaRendererKind === 'video' &&
      getRemoteVideoHolder(remoteVideoHolderRef)?.querySelector(
        `.participant_container_${CSS.escape(data.endpoint.id)}`
      ) !== null
    ) {
      removeNoVideoContainer(remoteVideoHolderRef, data.endpoint.id);
    }

    if (mediaRendererKind === 'video') {
      const videoContainer = document.createElement('div');
      videoContainer.classList.add(
        'video_container',
        `video_container_${data.endpoint.id}`,
        `video_container_${data.mediaRenderer.id}`
      );

      getRemoteVideoHolder(remoteVideoHolderRef)
        ?.querySelector(`.participant_container_${CSS.escape(data.endpoint.id)}`)
        ?.appendChild(videoContainer);

      data.mediaRenderer.render(videoContainer);
      fillUserInfoInVideoContainer(videoContainer, participantName);
    }
  }
}

export function handleRemoteMediaRemoved({
  data,
  remoteVideoHolderRef,
  endpointsMediaRef,
  participantName,
  isConferenceCall,
}: RemoteMediaParams) {
  if (!data.endpoint.isDefault || !isConferenceCall) {
    const indexMedia = endpointsMediaRef.current[data.endpoint.id].indexOf(data.mediaRenderer.kind);
    endpointsMediaRef.current[data.endpoint.id].splice(indexMedia, 1);

    if (
      endpointsMediaRef.current[data.endpoint.id].length &&
      endpointsMediaRef.current[data.endpoint.id].every((media) => media === 'audio')
    ) {
      addNoVideoContainer(remoteVideoHolderRef, data.endpoint.id, participantName);
    }

    if (data.mediaRenderer.kind !== 'audio') {
      getRemoteVideoHolder(remoteVideoHolderRef)
        ?.querySelector(`.video_container_${CSS.escape(data.mediaRenderer.id)}`)
        ?.remove();
    }
  }

  const removedMedia = document.querySelector(`.${CSS.escape(data.mediaRenderer.id)}`);
  removedMedia?.classList.add('inactive');
}

export function handleEndpointAdded({
  data,
  remoteVideoHolderRef,
  endpointsMediaRef,
  onRemoteMediaAdded,
  onRemoteMediaRemoved,
  onEndpointRemoved,
}: EndpointAddedParams) {
  endpointsMediaRef.current[data.endpoint.id] = [];

  const participantContainer = document.createElement('div');
  participantContainer.classList.add(
    'participant_container',
    `participant_container_${data.endpoint.id}`
  );

  getRemoteVideoHolder(remoteVideoHolderRef)?.appendChild(participantContainer);

  data.endpoint.addEventListener(VoxImplant.EndpointEvents.RemoteMediaAdded, onRemoteMediaAdded);
  data.endpoint.addEventListener(
    VoxImplant.EndpointEvents.RemoteMediaRemoved,
    onRemoteMediaRemoved
  );
  data.endpoint.addEventListener(VoxImplant.EndpointEvents.Removed, onEndpointRemoved);
}

export function handleEndpointRemoved({
  data,
  remoteVideoHolderRef,
  endpointsMediaRef,
  onRemoteMediaAdded,
  onRemoteMediaRemoved,
  onEndpointRemoved,
}: EndpointRemovedParams) {
  delete endpointsMediaRef.current[data.endpoint.id];

  data.endpoint.removeEventListener(VoxImplant.EndpointEvents.RemoteMediaAdded, onRemoteMediaAdded);
  data.endpoint.removeEventListener(
    VoxImplant.EndpointEvents.RemoteMediaRemoved,
    onRemoteMediaRemoved
  );
  data.endpoint.removeEventListener(VoxImplant.EndpointEvents.Removed, onEndpointRemoved);

  getRemoteVideoHolder(remoteVideoHolderRef)
    ?.querySelector(`.participant_container_${CSS.escape(data.endpoint.id)}`)
    ?.remove();
}
